/**
 * The Templates page: the firm's own policy templates and the ones published
 * by regulators or written by Amoreg for its jurisdictions. "Start from
 * template" opens a chat with eve, which fetches the template, fills its
 * blanks from the organisation's details, shows the draft and files it.
 *
 * Reached with `?for=<document name>` from a missing-document suggestion: the
 * templates that fit that document are listed first and the chat drafts it by
 * that name.
 *
 * @module components/regulation/templates/TemplatesSection
 */
import { Riffle } from "@lucasmarkes/hairline/react";
import {
  ExternalLinkIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  LayoutTemplateIcon,
  LinkIcon,
  MoreHorizontalIcon,
  PlusIcon,
  SparklesIcon,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { useStartChatWithText } from "../../../hooks/useStartChatWithText";
import { UNAVAILABLE_MESSAGES } from "../../../state/orgProfile";
import { usePolicyTemplates, type PolicyTemplate } from "../../../state/policyTemplates";
import { Button } from "../../ui/button";
import { HairlineArt } from "../../ui/hairline-art";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../../ui/menu";
import { Skeleton } from "../../ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { RegulationSectionPage } from "../RegulationSectionPage";
import { useRegulationOrg } from "../useRegulationOrg";
import { TemplateUploadDialog } from "./TemplateUploadDialog";
import {
  blanksLabel,
  canArchiveTemplate,
  groupTemplates,
  startFromTemplatePrompt,
  templateFormatLabel,
  templateSourceLabel,
  templatesForDocument,
} from "./templatesView";

function TemplateGlyph({ template }: { template: PolicyTemplate }) {
  const Icon =
    template.licence === "link_only"
      ? LinkIcon
      : template.format === "xlsx"
        ? FileSpreadsheetIcon
        : FileTextIcon;
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
      <Icon className="size-4" />
    </span>
  );
}

function EmptyPanel({ title, body, art }: { title: string; body: string; art?: ReactNode }) {
  return (
    <div className="mt-8 flex flex-col items-center rounded-xl border border-dashed px-6 py-16 text-center">
      {art ?? <LayoutTemplateIcon className="size-5 text-muted-foreground/60" />}
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function TemplateRow({
  template,
  canManage,
  canStart,
  onStart,
  onDownload,
  onArchive,
}: {
  template: PolicyTemplate;
  canManage: boolean;
  canStart: boolean;
  onStart: () => void;
  onDownload: () => void;
  onArchive: () => void;
}) {
  const blanks = blanksLabel(template);
  const hasFile = template.downloadPath !== null;
  return (
    <TableRow className="h-16">
      <TableCell className="px-4">
        <div className="flex min-w-0 items-center gap-3">
          <TemplateGlyph template={template} />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground">{template.title}</span>
              {template.policyType ? (
                <span className="hidden shrink-0 rounded-md border border-border/70 px-1.5 py-px text-[11px] leading-4 text-muted-foreground sm:inline">
                  {template.policyType}
                </span>
              ) : null}
            </div>
            <p className="truncate text-xs text-muted-foreground">
              {template.description ??
                (template.documentKind === "procedure" ? "Starts a procedure" : "Starts a policy")}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground md:table-cell">
        {templateSourceLabel(template)}
      </TableCell>
      <TableCell className="hidden px-4 text-xs text-muted-foreground md:table-cell">
        {templateFormatLabel(template)}
        {blanks ? (
          <span className="block text-[11px] text-muted-foreground/80">{blanks}</span>
        ) : null}
      </TableCell>
      <TableCell className="px-4">
        <div className="flex items-center justify-end gap-1.5">
          <Button
            type="button"
            size="xs"
            variant="outline"
            className="cursor-pointer gap-1"
            disabled={!canStart}
            title={
              canStart
                ? "Open a chat with eve to draft from this template"
                : "Chat is still loading"
            }
            onClick={onStart}
          >
            <SparklesIcon className="size-3" />
            Start from template
          </Button>
          <Menu>
            <MenuTrigger
              render={
                <Button
                  type="button"
                  size="icon-xs"
                  variant="ghost"
                  aria-label={`Actions for ${template.title}`}
                />
              }
            >
              <MoreHorizontalIcon className="size-4" />
            </MenuTrigger>
            <MenuPopup align="end">
              {hasFile ? <MenuItem onClick={onDownload}>Download</MenuItem> : null}
              {template.sourceUrl ? (
                <MenuItem onClick={() => window.open(template.sourceUrl!, "_blank", "noopener")}>
                  <ExternalLinkIcon className="size-3.5" />
                  Open the original
                </MenuItem>
              ) : null}
              {canArchiveTemplate(template, canManage) ? (
                <>
                  {hasFile || template.sourceUrl ? <MenuSeparator /> : null}
                  <MenuItem variant="destructive" onClick={onArchive}>
                    Archive
                  </MenuItem>
                </>
              ) : null}
            </MenuPopup>
          </Menu>
        </div>
      </TableCell>
    </TableRow>
  );
}

function TemplateTable({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {heading}
      </h2>
      {/* overflow-x-auto, never hidden: a clipped actions column is unusable. */}
      <div className="overflow-x-auto rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-b bg-muted/50 hover:bg-muted/50">
              <TableHead className="h-10 px-4 font-medium text-foreground/80">Template</TableHead>
              <TableHead className="hidden h-10 w-[160px] px-4 font-medium text-foreground/80 md:table-cell">
                From
              </TableHead>
              <TableHead className="hidden h-10 w-[130px] px-4 font-medium text-foreground/80 md:table-cell">
                Format
              </TableHead>
              <TableHead className="h-10 w-[220px] px-4">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{children}</TableBody>
        </Table>
      </div>
    </section>
  );
}

export function TemplatesSection({ forDocument }: { readonly forDocument?: string | null }) {
  const { org, isLoading } = useRegulationOrg();
  const organizationId = org?.id ?? null;
  const { snapshot, unavailableReason, isPending, upload, archive, download } =
    usePolicyTemplates(organizationId);
  const startChat = useStartChatWithText(organizationId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const templates = useMemo(() => snapshot?.templates ?? [], [snapshot]);
  const canManage = snapshot?.canManage ?? false;
  const wanted = forDocument?.trim() || null;
  const matching = useMemo(
    () => (wanted ? templatesForDocument(templates, wanted) : []),
    [templates, wanted],
  );
  const { own, shared } = groupTemplates(templates, matching);

  const row = (template: PolicyTemplate) => (
    <TemplateRow
      key={template.id}
      template={template}
      canManage={canManage}
      canStart={startChat !== null}
      onStart={() => void startChat?.(startFromTemplatePrompt(template, wanted))}
      onDownload={() => download(template.id)}
      onArchive={() => {
        setActionError(null);
        void archive(template.id).then((error) => setActionError(error));
      }}
    />
  );

  const body = (() => {
    if (
      (org === null && isLoading) ||
      (org !== null && isPending && snapshot === null && unavailableReason === null)
    ) {
      return (
        <div className="mt-6 space-y-2">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      );
    }
    if (org === null) {
      return (
        <EmptyPanel
          title="No organization selected"
          body="Choose an organization to see its templates."
        />
      );
    }
    if (unavailableReason !== null) {
      return (
        <EmptyPanel
          title="Templates are unavailable"
          body={UNAVAILABLE_MESSAGES[unavailableReason]}
        />
      );
    }
    if (templates.length === 0) {
      return (
        <EmptyPanel
          title="No templates yet"
          art={<HairlineArt figure={Riffle} label="Templates" className="mb-1 w-48 sm:w-56" />}
          body={
            canManage
              ? "Add your firm's own templates — Word, Excel or PDF — or save an existing policy as one from its page. eve starts new policies from them and fills the blanks from your organisation's details."
              : "Your organisation has no templates yet. An admin can add the firm's own, or save an existing policy as one."
          }
        />
      );
    }
    return (
      <>
        {wanted ? (
          matching.length > 0 ? (
            <TemplateTable heading={`For “${wanted}”`}>{matching.map(row)}</TemplateTable>
          ) : (
            <p className="mt-6 rounded-xl border border-dashed px-4 py-3 text-sm text-muted-foreground">
              No template matches “{wanted}”. Start from the closest one below, or ask eve to draft
              it from your organisation's details.
            </p>
          )
        ) : null}
        {own.length > 0 ? (
          <TemplateTable heading="Your firm's templates">{own.map(row)}</TemplateTable>
        ) : null}
        {shared.length > 0 ? (
          <TemplateTable heading="From regulators and Amoreg">{shared.map(row)}</TemplateTable>
        ) : null}
      </>
    );
  })();

  return (
    <RegulationSectionPage
      title="Templates"
      description="Starting points for new policies and procedures. eve fills each one from your organisation's details and tailors it before it is filed."
      emptyIcon={LayoutTemplateIcon}
      emptyTitle="No templates yet"
      emptyBody=""
      actions={
        canManage && organizationId ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="cursor-pointer gap-1.5"
            onClick={() => setUploadOpen(true)}
          >
            <PlusIcon className="size-3.5" />
            Add template
          </Button>
        ) : null
      }
    >
      {actionError !== null ? <p className="mt-4 text-sm text-destructive">{actionError}</p> : null}
      {body}
      {organizationId ? (
        <TemplateUploadDialog
          organizationId={organizationId}
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          upload={upload}
        />
      ) : null}
    </RegulationSectionPage>
  );
}
